import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Smile, SendHorizonal } from 'lucide-react';
import { io } from 'socket.io-client';
import Layout from '../../components/Layout';
import { AlertCircle } from 'lucide-react';

import ReactMarkdown from 'react-markdown';

import chatbotIcon from '../../assets/images/chatbot-icon.png';
import typingIcon from '../../assets/icons/typing.svg';
import { useStores } from '../../use.store';
import { getFormattedDate, getPoiAsset } from '../../helpers';
import { observer } from 'mobx-react-lite';
import GeneralInformationButton from '../../components/GeneralInformationButton';
import { Link, useNavigate } from 'react-router-dom';
import { checkMessage } from '../../utils/badWordsFilter';
import { checkIfChatCsAvailable } from '../../services/chatService';

const GeneralEnquiriesPage = observer(() => {
  const { mapStore, mainStore } = useStores();

  const queryParams = new URLSearchParams(window.location.search);
  const serverUrl =
    queryParams.get('serverUrl') || 'https://ai.api.test.ogwmaps.com/';
  const [ws, setWs] = useState(null);
  const [history, setHistory] = useState([]);
  const [stageType, setStageType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBeforeNav, setIsLoadingBeforeNav] = useState(false);

  const [text, setText] = useState('');
  const [isIntroExist, setIsIntroExist] = useState(false);

  const [websocketError, setWebsocketError] = useState();

  const [canAccessCustomerServiceChat, setCanAccessCustomerServiceChat] =
    useState(false);

  const [isFirstInit, setIsFirstInit] = useState(true);

  const canAccessRef = useRef(false);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Use setTimeout to ensure DOM has updated before scrolling
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [mainStore.chatHistory]);

  // Also scroll when bot typing status changes
  useEffect(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [mainStore.isBotTyping]);

  useEffect(() => {
    const callApi = async () => {
      try {
        const response = await checkIfChatCsAvailable(
          mainStore.config.baseUrl,
          mainStore.config.adsignId,
        );

        const schedule = response?.data?.[0];

        if (!schedule) {
          canAccessRef.current = false;
          setCanAccessCustomerServiceChat(false);
          return;
        }

        const now = new Date();

        const startTime = new Date(schedule.start);
        const endTime = new Date(schedule.end);

        // =========================
        // CHECK TIME (UTC)
        // =========================
        const nowTimeValue =
          now.getUTCHours() * 3600000 +
          now.getUTCMinutes() * 60000 +
          now.getUTCSeconds() * 1000;

        const startTimeValue =
          startTime.getUTCHours() * 3600000 +
          startTime.getUTCMinutes() * 60000 +
          startTime.getUTCSeconds() * 1000;

        const endTimeValue =
          endTime.getUTCHours() * 3600000 +
          endTime.getUTCMinutes() * 60000 +
          endTime.getUTCSeconds() * 1000;

        const checkTime =
          nowTimeValue >= startTimeValue && nowTimeValue <= endTimeValue;

        // =========================
        // CHECK DAY (UTC)
        // =========================
        const dayMap = {
          0: 'sunday',
          1: 'monday',
          2: 'tuesday',
          3: 'wednesday',
          4: 'thursday',
          5: 'friday',
          6: 'saturday',
        };

        const currentDay = dayMap[now.getUTCDay()];

        const checkDay = schedule.days?.includes(currentDay);
        const checkSchedule = checkDay && checkTime;

        console.log({
          currentDay,
          scheduleDays: schedule.days,
          checkDay,
          checkTime,
          checkSchedule,
          now: now.toISOString(),
          start: schedule.start,
          end: schedule.end,
        });

        if (schedule.isEnabled) {
          canAccessRef.current = checkSchedule;
          setCanAccessCustomerServiceChat(checkSchedule);
        } else {
          canAccessRef.current = false;
          setCanAccessCustomerServiceChat(false);
        }
      } catch (error) {
        canAccessRef.current = false;
        setCanAccessCustomerServiceChat(false);
      }
    };

    callApi();
  }, []);

  useEffect(() => {
    // if (!mainStore.isChatbotStart) return;
    try {
      const socketConnection = io(serverUrl, {
        transports: ['websocket'],
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socketConnection.on('connect', () => {
        console.log('Connected to Socket.IO server');

        const channelId = '01984f5b-f06c-7223-9a54-4afaf46202c7';

        setTimeout(async () => {
          socketConnection.emit('deviceId', {
            channelId,
          });
        }, 1000);
      });

      socketConnection.on('message', (data) => {
        const response = JSON.parse(data);
        handleResponse(response);
      });

      socketConnection.on('disconnect', (err) => {
        console.error('Socket.IO connection error:', err);
      });

      socketConnection.on('connect_error', (error) => {
        socketConnection.disconnect();
        // setWebsocketError(
        //   "Failed to connect to the server. Please try again later."
        // );
      });

      setWs(socketConnection);

      return () => {
        socketConnection.disconnect();
        console.log('Disconnected from Socket.IO server');
      };
    } catch (error) {
      console.log('Error connecting to Socket.IO server:', error);
    }
  }, []);

  useEffect(() => {
    let greetingExists = false;
    const getGreeting = () => {
      const hour = new Date().getHours();

      if (hour >= 5 && hour < 12) return 'Good morning';
      if (hour >= 12 && hour < 15) return 'Good afternoon';
      if (hour >= 15 && hour < 18) return 'Good evening';
      return 'Good night';
    };

    mainStore?.chatHistory?.map((data) => {
      if (data?.greeting) {
        greetingExists = true;
      }
    });

    if (greetingExists) return;
    mainStore.setChatHistory({
      chatMsg: `${getGreeting()}, visitor! I'm SBGbot, your friendly virtual Visitor Services guide. How may I assist you today?`,
      chatMsgTime: getFormattedDate(),
      isUser: false,
      greeting: true,
    });
    setIsFirstInit(false);
  }, []);

  const sendMessage = (userTyping) => {
    setIsLoading(true);
    const foundInappropriate = checkMessage(userTyping ?? text);

    if (foundInappropriate.length > 0) {
      setIsLoading(false);
      alert(
        `Your message contains inappropriate content and cannot be sent. Detected words: ${foundInappropriate.join(
          ', ',
        )}`,
      );
      return;
    }

    const data = {
      user_question: userTyping ?? text,
      deviceId: '01984f5b-f06c-7223-9a54-4afaf46202c7',
    };

    if (data.user_question !== '') {
      setHistory((prevHistory) => {
        if (
          prevHistory.length > 0 &&
          prevHistory[prevHistory.length - 1].includes('You: isLoading')
        ) {
          return [...prevHistory.slice(0, -1), `You: ${userTyping ?? text}`];
        }
        return [...prevHistory, `You: ${userTyping ?? text}`];
      });

      const stage = handleStage({ stage: 'sending' });
      setStageType(stage);

      const newChat = {
        chatMsg: userTyping ?? text,
        chatMsgTime: getFormattedDate(),
        isUser: true,
      };
      mainStore.setChatHistory(newChat);

      if (ws) {
        mainStore.setIsBotTyping(true);
        setText('');
        setTimeout(async () => {
          ws.emit('message', JSON.stringify(data));
        }, 1000);
      }
    } else {
      setIsLoading(false);
      setHistory((prevHistory) => {
        if (
          prevHistory &&
          prevHistory.length > 0 &&
          prevHistory[prevHistory.length - 1].includes('You:')
        ) {
          return prevHistory.slice(0, -1);
        }
      });
    }
  };

  const handleResponse = (response) => {
    mainStore.setIsBotTyping(false);
    setText('');
    if (response && response.data) {
      setHistory((prevHistory) => {
        const updatedHistory = [...prevHistory];

        if (
          updatedHistory[updatedHistory.length - 1]?.includes(
            'Chatbot: isLoading',
          )
        ) {
          updatedHistory[updatedHistory.length - 1] =
            `Chatbot: ${response['data']['chatbot']}`;
        } else {
          updatedHistory.push(`Chatbot: ${response['data']['chatbot']}`);
          setIsIntroExist(true);
        }

        if (response['data']['poi_list']) {
          updatedHistory.push(
            `poiList: ${JSON.stringify(response['data']['poi_list'])}`,
          );
        }

        if (response['data']['chatbot']) {
          const chatBot = mainStore.chatHistory.filter((chat) => !chat.isUser);

          const newChat = {
            chatMsg: response['data']['chatbot'],
            chatMsgTime: getFormattedDate(),
            isUser: false,
            poiList: response['data']['poi_list'] ?? [],
          };
          mainStore.setChatHistory(newChat);
          updatedHistory.push(`chatbot: ${response['data']['chatbot']}`);

          if (canAccessRef.current) {
            setTimeout(() => {
              if (chatBot.length > 2) {
                const newChat = {
                  chatMsg:
                    'Did that answer your question, or would you like me to connect you with a live agent?',
                  chatMsgTime: getFormattedDate(),
                  isUser: false,
                  showOptionToChatCS: true,
                  isUserResponseToChatCSOption: false,
                };
                mainStore.setChatHistory(newChat);
                updatedHistory.push(`chatbot: ${response['data']['chatbot']}`);
              }
            }, 2000);
          }
        }

        return updatedHistory;
      });
    }
    setIsLoading(false);

    setStageType('');
  };

  const handleStage = (response) => {
    if (response && response.stage) {
      const phases = {
        sending: 'Please Wait',
        object: 'Please Wait',
        message: 'Please Wait',
        translate: 'Please Wait',
      };

      return phases[response.stage];
    }
  };

  const navigate = useNavigate();

  const navigateToChatRoom = () => {
    const disconnectSocket = async () => {
      if (ws) {
        await ws.removeAllListeners();
        await ws.disconnect();
        setIsLoadingBeforeNav(true);
        await new Promise((resolve) => setTimeout(resolve, 3000));
        setIsLoadingBeforeNav(false);
        navigate('/chat-customer-service');
      }
    };
    disconnectSocket();
  };

  const getPoiInformation = (poiId) => {
    const filteredPoi = mapStore.pois.filter((poi) => poi._id === poiId);
    return filteredPoi;
  };

  return (
    <Layout>
      {/* Header */}
      <div className="w-full bg-green-900 text-white flex items-center justify-center py-4 border-b-2 border-blue-400 h-[62px]">
        <h1 className="text-lg font-semibold">Ask SBGbot</h1>
      </div>
      {websocketError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 m-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-4 h-4" />
            <span className="font-medium">Error:</span>
          </div>
          <p className="text-red-700 mt-1">{websocketError}</p>
        </div>
      )}

      {/* Chat Content */}
      {!websocketError && (
        <div className="flex flex-col h-full items-start w-full max-w-md p-4 h-4/5 overflow-y-auto">
          {mainStore?.chatHistory?.map((chat) => {
            return (
              <div className="flex flex-col w-full">
                {chat.isUser ? (
                  <div className="flex flex-col items-end mb-2">
                    <div className="w-full pr-4">
                      <div className="bg-[#75787B] text-white p-4 rounded-xl rounded-br-none">
                        <p>{chat.chatMsg}</p>
                      </div>
                      <div className="flex flex-row justify-start">
                        <p className="text-xs text-gray-500 mt-1">
                          {chat.chatMsgTime}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-start mb-2">
                    <div className="pl-4">
                      <div className="bg-[#215732] text-white p-3 sm:p-4 md:p-5 rounded-xl rounded-bl-none w-full overflow-hidden">
                        <div
                          className="
                              prose prose-sm sm:prose-base lg:prose-lg
                              max-w-full
                              break-words
                              overflow-x-auto
                            "
                        >
                          <ReactMarkdown
                            components={{
                              h3: ({ node, ...props }) => (
                                <h3
                                  className="
                                    text-base sm:text-lg font-bold text-white
                                    mt-4 sm:mt-6 mb-2 sm:mb-3
                                    first:mt-0
                                    break-words
                                  "
                                  {...props}
                                />
                              ),

                              p: ({ node, ...props }) => (
                                <p
                                  className="
                                    text-white
                                    mb-3 sm:mb-4
                                    leading-relaxed
                                    text-sm sm:text-base
                                    break-words
                                  "
                                  {...props}
                                />
                              ),

                              ol: ({ node, ...props }) => (
                                <ol
                                  className="
                                    list-decimal
                                    ml-5 sm:ml-6
                                    mb-4
                                    space-y-2
                                  "
                                  {...props}
                                />
                              ),

                              ul: ({ node, ...props }) => (
                                <ul
                                  className="
                                  list-disc
                                  ml-5 sm:ml-8
                                  space-y-1.5
                                "
                                  {...props}
                                />
                              ),

                              li: ({ node, ...props }) => (
                                <li
                                  className="
                                    text-white
                                    leading-relaxed
                                    text-sm sm:text-base
                                    break-words
                                  "
                                  {...props}
                                />
                              ),

                              strong: ({ node, ...props }) => (
                                <strong
                                  className="font-bold text-white"
                                  {...props}
                                />
                              ),

                              a: ({ node, ...props }) => (
                                <a
                                  className="
                                    text-[#D9B77B]
                                    underline
                                    break-all
                                  "
                                  {...props}
                                />
                              ),

                              code: ({ node, inline, ...props }) =>
                                inline ? (
                                  <code
                                    className="
                                    bg-black/20
                                    px-1 py-0.5
                                    rounded
                                    text-xs sm:text-sm
                                    break-all
                                  "
                                    {...props}
                                  />
                                ) : (
                                  <pre
                                    className="
                                      overflow-x-auto
                                      rounded-lg
                                      bg-black/20
                                      p-3
                                      text-xs sm:text-sm
                                    "
                                  >
                                    <code {...props} />
                                  </pre>
                                ),
                            }}
                          >
                            {chat?.chatMsg?.replace(/\\n/g, '\n')}
                          </ReactMarkdown>
                        </div>
                      </div>

                      <div className="flex flex-row justify-end">
                        <p className="text-xs text-gray-500 mt-1">
                          {chat.chatMsgTime}
                        </p>
                      </div>
                    </div>
                    {/* Avatar */}
                    <div className="flex flex-row">
                      {/* <div className="bg-yellow-100 rounded-full flex items-center justify-center mr-2 mt-[-20px] w-12 h-max">
                        <img src={chatbotIcon} className="w-10 h-10" />
                      </div> */}
                      {canAccessCustomerServiceChat &&
                        chat?.showOptionToChatCS && (
                          <div className="flex flex-col gap-y-1 w-full mt-[4px]">
                            <div
                              onClick={() => navigateToChatRoom()}
                              className="flex flex-row justify-center items-center bg-[#A09200] rounded rounded-md text-white p-2 text-[10px] w-max"
                            >
                              I wish to have a chat with Customer Service
                            </div>

                            <div
                              onClick={() => {
                                if (chat.isUserResponseToChatCSOption) return;
                                mainStore.chatHistory
                                  ?.filter(
                                    (tempChat) =>
                                      tempChat.chatMsgTime ===
                                        chat.chatMsgTime &&
                                      tempChat.chatMsg === chat.chatMsg,
                                  )
                                  ?.map((chat) => {
                                    chat.isUserResponseToChatCSOption = true;
                                  });
                                mainStore.setChatHistory({
                                  chatMsg: `Okay, I’ll continue assisting you here 👍`,
                                  chatMsgTime: getFormattedDate(),
                                  isUser: false,
                                });
                              }}
                              className="flex flex-row justify-center items-center bg-[#A09200] rounded rounded-md text-white p-2 text-[10px] w-[165px]"
                            >
                              No, I do not wish to have a chat
                            </div>
                          </div>
                        )}
                    </div>

                    {/* <div className="pl-4 mt-2">
                      <div className="grid grid-cols-3 gap-x-2 gap-y-3">
                        {chat?.poiList?.map((poi) => {
                          // getPoiInformation
                          const poiInfo = getPoiInformation(poi.id);
                          return (
                            <div className="flex flex-col rounded-md p-1 bg-white shadow-md gap-y-2">
                              <Link
                                key={poiInfo[0]._id}
                                to={`/wayfinder?poiDestination=${poiInfo[0]._id}`}
                              >
                                <>
                                  <img
                                    src={getPoiAsset(
                                      mainStore.config,
                                      poiInfo[0],
                                    )}
                                    className="w-full h-[70px] object-cover object-center rounded-md"
                                  />
                                  <span className="text-sm text-black">
                                    {poiInfo[0].name}
                                  </span>
                                </>
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    </div> */}
                  </div>
                )}
              </div>
            );
          })}

          {mainStore.isBotTyping && (
            <div className="flex flex-col w-full">
              <div className="flex flex-col items-start mb-2">
                <div className="pl-4">
                  <div className="bg-[#215732] text-white p-4 rounded-xl rounded-bl-none">
                    <img src={typingIcon} />
                  </div>
                  <div className="flex flex-row justify-end"></div>
                </div>
                {/* Avatar */}
              </div>
            </div>
          )}

          <div ref={messagesEndRef}> </div>
        </div>
      )}

      {/* Quick Buttons */}
      {!websocketError && (
        <div className="flex justify-center items-center h-[200px] overflow-y-auto pt-2">
          <GeneralInformationButton
            onSelectInformation={(e) => sendMessage(e)}
          />
        </div>
      )}

      {/* Chat Input */}
      {!websocketError && (
        <div className="w-full max-w-md bg-white flex items-center gap-2 px-4 p-4 border-t mt-2">
          {/* <Smile className="text-gray-500 w-5 h-5" /> */}
          <input
            type="text"
            placeholder="Ask anything..."
            className="flex-1 px-4 py-2 text-base"
            onChange={(e) => setText(e.target.value)}
            value={text}
          />
          {!isLoading && (
            <button
              className="bg-green-900 text-white p-3 rounded-full"
              onClick={() => {
                if (!text.trim()) return;
                sendMessage();
              }}
            >
              <SendHorizonal className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {isLoadingBeforeNav && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4 shadow-2xl">
            <Loader2 className="w-16 h-16 text-green-600 animate-spin" />
          </div>
        </div>
      )}
    </Layout>
  );
});

export default GeneralEnquiriesPage;
